import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

import type { LifeUploadedDocumentMap } from "@/D2C/features/life-quote/redux/lifeQuoteSlice";
import type { TravelDocumentUploadValues } from "@/D2C/features/travel-quote/redux/type";
import { App_Url_New } from "@/shared/common/static";
import { ConfirmationCheckbox } from "@/shared/components";
import { DocumentUploadGrid } from "@/shared/components/DocumentUploadGrid";
import type { StepFormComponentProps, StepFormHandle } from "@/shared/stepper/types";
import type {
  DocumentUploadField,
  FieldKey,
  PersistedUploadedDocument,
} from "@/shared/types/documentuploadtype";

const LIFE_DOCUMENT_FIELDS: Array<DocumentUploadField<TravelDocumentUploadValues>> = [
  {
    key: "aadhaarCard",
    label: "Aadhaar Card",
    title: "Aadhaar Card",
    helper: "Click to upload (PDF / JPG / PNG)",
    required: false,
    icon: App_Url_New.DocumentIcons.idCard,
    enableOcr: false,
  },
  {
    key: "panCard",
    label: "PAN Card",
    title: "PAN Card",
    helper: "Click to upload (PDF / JPG / PNG)",
    required: false,
    icon: App_Url_New.DocumentIcons.idCard,
    enableOcr: false,
  },
];

const DOCUMENT_GUIDELINES = [
  "Accepted formats: PDF, JPG, PNG",
  "Maximum file size: 5 MB per document",
  "Ensure all details are clearly visible",
  "Blurry or cropped documents may delay approval",
];

const DEFAULT_FORM_VALUES: TravelDocumentUploadValues = {
  passport: false,
  passportBack: false,
  visaCopy: false,
  emiratesIdFront: false,
  emiratesIdBack: false,
  aadhaarCard: false,
  panCard: false,
  confirmDocuments: false,
};

type LifeDocumentUploadStepExtraProps = {
  isReadOnly?: boolean;
  persistedDocuments?: LifeUploadedDocumentMap;
  onUploadComplete?: (params: {
    field: DocumentUploadField<TravelDocumentUploadValues>;
    file: File;
    uploadResponse?: unknown;
    ocrResponse?: unknown;
    persistedDocument: PersistedUploadedDocument<FieldKey<TravelDocumentUploadValues>>;
  }) => void;
  isShowUploadGuidelines?: boolean;
  showRequiredIdentificationTitle?: boolean;
  onConfirmDocumentsChange?: (checked: boolean) => boolean | Promise<boolean> | void;
  enableAutoConfirmWhenRequiredUploaded?: boolean;
};

const LifeDocumentUploadStep = forwardRef<
  StepFormHandle<TravelDocumentUploadValues>,
  StepFormComponentProps<TravelDocumentUploadValues, LifeDocumentUploadStepExtraProps>
>(function LifeDocumentUploadStep(
  {
    defaultValues: incomingValues,
    isReadOnly = false,
    persistedDocuments,
    onUploadComplete,
    isShowUploadGuidelines = true,
    showRequiredIdentificationTitle = true,
    onConfirmDocumentsChange,
    enableAutoConfirmWhenRequiredUploaded = false,
  },
  ref
) {
  const [form, setForm] = useState<TravelDocumentUploadValues>(
    incomingValues ?? DEFAULT_FORM_VALUES
  );
  const [hasSubmitAttempted, setHasSubmitAttempted] = useState(false);
  const [showConfirmError, setShowConfirmError] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<Partial<Record<string, string>>>({});

  useEffect(() => {
    setForm(incomingValues ?? DEFAULT_FORM_VALUES);
    setHasSubmitAttempted(false);
    setShowConfirmError(false);
    setUploadErrors({});
  }, [incomingValues]);

  useEffect(() => {
    if (!persistedDocuments) {
      return;
    }

    setForm((current) => {
      const nextValues = { ...current };
      LIFE_DOCUMENT_FIELDS.forEach((field) => {
        if (persistedDocuments[field.key]) {
          nextValues[field.key] = true;
        }
      });
      return nextValues;
    });
  }, [persistedDocuments]);

  const getUploadValidationErrors = (values: TravelDocumentUploadValues) =>
    LIFE_DOCUMENT_FIELDS.reduce<Partial<Record<string, string>>>((errors, field) => {
      if (field.required && values[field.key] !== true) {
        errors[field.key] = `${field.label || field.title} is required.`;
      }
      return errors;
    }, {});

  const shouldAutoConfirmDocuments = (values: TravelDocumentUploadValues) =>
    LIFE_DOCUMENT_FIELDS.filter((field) => field.required).every(
      (field) => values[field.key] === true
    );

  const normalizeUploadedValues = (values: TravelDocumentUploadValues) => {
    if (
      !enableAutoConfirmWhenRequiredUploaded ||
      values.confirmDocuments === true ||
      !shouldAutoConfirmDocuments(values)
    ) {
      return values;
    }

    void onConfirmDocumentsChange?.(true);
    return {
      ...values,
      confirmDocuments: true,
    };
  };

  const syncValidationState = (nextValues: TravelDocumentUploadValues) => {
    if (!hasSubmitAttempted) {
      return;
    }

    setUploadErrors(getUploadValidationErrors(nextValues));
    setShowConfirmError(nextValues.confirmDocuments !== true);
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      const nextUploadErrors = getUploadValidationErrors(form);
      const isConfirmValid = form.confirmDocuments === true;

      setHasSubmitAttempted(true);
      setUploadErrors(nextUploadErrors);
      setShowConfirmError(!isConfirmValid);

      return Object.keys(nextUploadErrors).length === 0 && isConfirmValid;
    },
    getValues: () => form,
  }));

  return (
    <div className="space-y-6">
      {showRequiredIdentificationTitle ? (
        <div className="mb-4">
          <h2 className="text-lg text-dark">Required Identification</h2>
        </div>
      ) : null}

      <fieldset disabled={isReadOnly} className="contents">
        <DocumentUploadGrid<TravelDocumentUploadValues>
          fields={LIFE_DOCUMENT_FIELDS}
          values={form}
          disabled={isReadOnly}
          onChange={(nextValues) => {
            const normalizedValues = normalizeUploadedValues(nextValues);
            setForm(normalizedValues);
            syncValidationState(normalizedValues);
          }}
          fieldErrors={uploadErrors}
          uploadEndpoint={App_Url_New.ApiEndPoint.documentUpload}
          ocrEndpointPath={App_Url_New.ApiOcrEndPoint.ExtractDocument}
          ocrFields={{
            ai_engine: "openai",
            ocr_engine: "easy",
          }}
          initialDocuments={persistedDocuments}
          onUploadComplete={onUploadComplete}
        />
      </fieldset>

      {isShowUploadGuidelines ? (
        <div className="mt-5 rounded-xl border border-theme-border bg-theme-surface-subtle p-4">
          <h3 className="text-sm font-semibold text-theme-text">Document guidelines</h3>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-[11px] text-theme-text-soft">
            {DOCUMENT_GUIDELINES.map((guideline) => (
              <li className="mb-0.5" key={guideline}>
                {guideline}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <fieldset disabled={isReadOnly} className="contents">
        <ConfirmationCheckbox
          checked={form.confirmDocuments === true}
          disabled={isReadOnly}
          error={showConfirmError ? "Please confirm the document upload before proceeding." : ""}
          onChange={async (checked) => {
            if (checked) {
              const nextUploadErrors = getUploadValidationErrors(form);
              const hasValidationErrors = Object.keys(nextUploadErrors).length > 0;

              setHasSubmitAttempted(true);
              setUploadErrors(nextUploadErrors);

              if (hasValidationErrors) {
                setShowConfirmError(true);
                setForm((current) => ({
                  ...current,
                  confirmDocuments: false,
                }));
                return;
              }
            }

            const nextValues = { ...form, confirmDocuments: checked };
            setForm(nextValues);
            syncValidationState(nextValues);
            queueMicrotask(async () => {
              const accepted = await onConfirmDocumentsChange?.(checked);
              if (checked && accepted === false) {
                setForm((current) => {
                  const revertedValues = { ...current, confirmDocuments: false };
                  syncValidationState(revertedValues);
                  return revertedValues;
                });
              }
            });
          }}
        >
          I confirm that I have uploaded all required documents.
        </ConfirmationCheckbox>
      </fieldset>
    </div>
  );
});

export { LifeDocumentUploadStep };
export default LifeDocumentUploadStep;
